import { LocalStorageService } from '@/services/storage/local-storage-service';
import { inject, provide, ref, type InjectionKey, type Ref } from 'vue';

export const LIST_MIN_WIDTH_PX = 200;
export const POOL_PANE_MIN_WIDTH_PX = 240;
const SPLITTER_WIDTH_PX = 24;

export type FileExplorerViewMode = 'details';
export type FileExplorerIconSize = 'small' | 'medium' | 'large';

export interface FileManagerSettingsStorageState {
  listWidthPx: number;
  poolWeights: Record<string, number>;
  iconSize: FileExplorerIconSize;
  viewMode: FileExplorerViewMode;
}

const DEFAULT_LIST_WIDTH_PX = 280;
const DEFAULT_POOL_WEIGHTS: Record<string, number> = {
  'file-explorer-view': 100,
  'details-view': 50,
};
const DEFAULT_ICON_SIZE: FileExplorerIconSize = 'medium';
const DEFAULT_VIEW_MODE: FileExplorerViewMode = 'details';

class FileManagerSettingsStorageService extends LocalStorageService<FileManagerSettingsStorageState> {
  constructor() {
    super('file-explorer-settings', () => ({
      listWidthPx: DEFAULT_LIST_WIDTH_PX,
      poolWeights: { ...DEFAULT_POOL_WEIGHTS },
      iconSize: DEFAULT_ICON_SIZE,
      viewMode: DEFAULT_VIEW_MODE,
    }));
  }

  load() {
    return this.getState();
  }

  save(partial: Partial<FileManagerSettingsStorageState>) {
    return this.setState(partial);
  }
}

export const fileManagerSettingsStorageService = new FileManagerSettingsStorageService();

export type FileManagerSettingsState = {
  listWidthPx: Ref<number>;
  poolWeights: Ref<Record<string, number>>;
  iconSize: Ref<FileExplorerIconSize>;
  viewMode: Ref<FileExplorerViewMode>;
  resizeSidebar: (deltaPx: number, containerWidthPx: number) => void;
  resizePoolPanes: (
    idA: string,
    idB: string,
    deltaPx: number,
    startRectA: DOMRect,
    startRectB: DOMRect,
  ) => void;
  persistLayout: () => void;
  setIconSize: (size: FileExplorerIconSize) => void;
  setViewMode: (mode: FileExplorerViewMode) => void;
};

export const fileManagerSettingsKey: InjectionKey<FileManagerSettingsState> =
  Symbol('fileManagerSettings');

export const provideFileManagerSettings = () => {
  const initialState = fileManagerSettingsStorageService.load();
  const listWidthPx = ref(initialState.listWidthPx);
  const poolWeights = ref<Record<string, number>>({ ...initialState.poolWeights });
  const iconSize = ref<FileExplorerIconSize>(initialState.iconSize);
  // Guards against a pre-existing localStorage blob still holding a 'grid'/'list' value from
  // before those modes were removed — the stored JSON isn't checked against the narrowed type.
  const viewMode = ref<FileExplorerViewMode>(
    initialState.viewMode === 'details' ? initialState.viewMode : DEFAULT_VIEW_MODE,
  );

  const resizeSidebar = (deltaPx: number, containerWidthPx: number) => {
    const poolPaneCount = Object.keys(poolWeights.value).length;
    const maxWidthPx =
      containerWidthPx -
      POOL_PANE_MIN_WIDTH_PX * poolPaneCount -
      SPLITTER_WIDTH_PX * poolPaneCount;
    listWidthPx.value = Math.min(
      Math.max(listWidthPx.value + deltaPx, LIST_MIN_WIDTH_PX),
      Math.max(maxWidthPx, LIST_MIN_WIDTH_PX),
    );
  };

  const resizePoolPanes = (
    idA: string,
    idB: string,
    deltaPx: number,
    startRectA: DOMRect,
    startRectB: DOMRect,
  ) => {
    const oldWeightA = poolWeights.value[idA] ?? 0;
    const oldWeightB = poolWeights.value[idB] ?? 0;
    const weightSum = oldWeightA + oldWeightB;

    const newPxA = Math.max(startRectA.width + deltaPx, POOL_PANE_MIN_WIDTH_PX);
    const newPxB = Math.max(startRectB.width - deltaPx, POOL_PANE_MIN_WIDTH_PX);

    const newWeightA = weightSum * (newPxA / (newPxA + newPxB));
    const newWeightB = weightSum - newWeightA;

    poolWeights.value = {
      ...poolWeights.value,
      [idA]: newWeightA,
      [idB]: newWeightB,
    };
  };

  const persistLayout = () => {
    fileManagerSettingsStorageService.save({
      listWidthPx: listWidthPx.value,
      poolWeights: poolWeights.value,
    });
  };

  const setIconSize = (size: FileExplorerIconSize) => {
    iconSize.value = size;
    fileManagerSettingsStorageService.save({ iconSize: size });
  };

  const setViewMode = (mode: FileExplorerViewMode) => {
    viewMode.value = mode;
    fileManagerSettingsStorageService.save({ viewMode: mode });
  };

  const state: FileManagerSettingsState = {
    listWidthPx,
    poolWeights,
    iconSize,
    viewMode,
    resizeSidebar,
    resizePoolPanes,
    persistLayout,
    setIconSize,
    setViewMode,
  };
  provide(fileManagerSettingsKey, state);
  return state;
};

export const useFileManagerSettings = () => {
  const state = inject(fileManagerSettingsKey);
  if (!state) {
    throw new Error(
      'useFileManagerSettings() must be called within a component tree provided by provideFileManagerSettings()',
    );
  }
  return state;
};
